import React, { useEffect } from 'react';
import { ConfigProvider } from 'antd';
import PageNotFound from 'components/shared/Error';
import { IRoute } from 'interfaces/routesInt';
import AuthMiddleware from 'middlewares/authMiddleware';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import routes from 'routes';
import { usePlatformConfig } from 'hooks/PlatformContext';
import { CurrencyProvider } from 'hooks/userCurrency';
import { useLanguage } from 'hooks/Languages/useLanguage';
import frFR from 'antd/locale/fr_FR';
import enUS from 'antd/locale/en_US';
import { BranchProvider } from 'context/BranchContext';
import { useFetchUserData } from 'queries/auth/user';
import { getBusinessTheme } from 'utils/themeColors';
import { getNormalizedBusinessType } from 'utils/navigation';
import SupportWidget from 'components/shared/SupportWidget';

function App() {
  const platformItem = usePlatformConfig();
  const { currentLanguage } = useLanguage();
  const locale = currentLanguage === 'fr' ? frFR : enUS;

  const { userData } = useFetchUserData();
  const planType = (
    userData?.shop?.plan_type ||
    userData?.plan_type ||
    localStorage.getItem('plan_type') ||
    ''
  ).toLowerCase();
  const isPureLite = planType === 'lite';
  const normalizedType = getNormalizedBusinessType(userData);
  const rawBusinessType = userData?.shop?.business_type || userData?.business_type;
  const t = getBusinessTheme(normalizedType || rawBusinessType, isPureLite);

  // Apply the full design token set as CSS custom properties on the root
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary-color', t.primary);
    root.style.setProperty('--primary-color-dark-5', t.primaryDark);
    root.style.setProperty('--primary-color-dark-10', t.primaryDarker);
    root.style.setProperty('--primary-color-light-5', t.primaryLight);
    root.style.setProperty('--primary-color-light-10', t.primaryLighter);
    root.style.setProperty('--primary-container', t.primaryContainer);
    root.style.setProperty('--color-secondary', t.secondary);
    root.style.setProperty('--sidebar-bg', t.sidebarBg);
    root.style.setProperty('--sidebar-active', t.sidebarActive);
    root.style.setProperty('--card-border', t.cardBorder);
  }, [t]);

  useEffect(() => {
    const loginState = localStorage.getItem('newLogin');
    if (loginState === 'YES') {
      const timeout = setTimeout(() => {
        localStorage.setItem('newLogin', 'NO');
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, []);

  return (
    <ConfigProvider
      locale={locale}
      theme={{
        token: {
          colorPrimary: t.primary,
          colorLink: t.primary,
          colorLinkHover: t.primaryLight,
          colorBorderSecondary: t.cardBorder,
        },
        components: {
          Button: {
            colorPrimary: t.primary,
            colorPrimaryHover: t.primaryDark,
            colorPrimaryActive: t.primaryDarker,
          },
          Menu: {
            itemActiveBg: t.sidebarActive,
            itemSelectedBg: t.sidebarActive,
            itemSelectedColor: '#ffffff',
            itemColor: '#94a3b8',
            itemHoverColor: '#f8fafc',
          },
          Tabs: {
            inkBarColor: t.primary,
            itemSelectedColor: t.primary,
          },
        },
      }}
    >
      <div className="App">
    <BranchProvider>
      <CurrencyProvider>
        <BrowserRouter basename="/" future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            {routes.map((route: IRoute, index: number) => {
              return route.isProtected ? (
                <Route
                  key={index}
                  element={
                    <AuthMiddleware isAuthProtected={route.isProtected} />
                  }
                >
                  <Route
                    path={route.path}
                    key={route.key}
                    index={route.index}
                    element={route.element}
                  />
                </Route>
              ) : (
                <Route
                  path={route.path}
                  key={route.key}
                  index={route.index || true}
                  element={route.element}
                />
              );
            })}
            <Route path="*" element={<PageNotFound />} />
          </Routes>
          {userData && <SupportWidget />}
        </BrowserRouter>
      </CurrencyProvider>
    </BranchProvider>
      </div>
    </ConfigProvider>
  );
}

export default App;
